import type { CustomFieldDto, EntityDto } from "../dto";
import type { ISelectListItem, RydeUiUnit } from "../models";
import { sortArrayAlphabetically } from "./array.helpers";
import { toTranslatedSelectListItem } from "./dto-transform.helpers";
import { removeMetadataPrefix } from "./dynamic-forms.helpers";

export const getFormLabelKey = (
  entity: RydeUiUnit,
  fieldName: string,
): string => `${entity}.formLabel.${removeMetadataPrefix(fieldName)}`;

export const getGridLabelKey = (
  entity: RydeUiUnit,
  fieldName: string,
): string => `${entity}.gridLabel.${removeMetadataPrefix(fieldName)}`;

export const getEntitySingleLabelKey = (entity: string): string =>
  `${entity}.single`;

/**
 * Translated label per custom field id. A field without a translation, or whose entity is not
 * loaded, keeps its code name. The trailing colon some form labels carry ("ID:") is dropped.
 */
export const buildCustomFieldTitleById = ({
  customFields,
  entityNameById,
  getLabelKey,
  translate,
}: {
  customFields: Pick<CustomFieldDto, "id" | "name" | "entityId">[];
  entityNameById: Map<number, RydeUiUnit>;
  getLabelKey: (entity: RydeUiUnit, fieldName: string) => string;
  translate: (key: string, defaultValue: string) => string;
}): Map<number, string> =>
  new Map(
    customFields.map(({ id, name, entityId }) => {
      const entityName = entityNameById.get(entityId);
      const title = entityName
        ? translate(getLabelKey(entityName, name), name)
        : name;

      return [id, title.replace(/:\s*$/, "") || name];
    }),
  );

export const buildEntitySelectItems = ({
  entities,
  translate,
}: {
  entities: Pick<EntityDto, "id" | "name">[];
  translate: (key: string, defaultValue: string) => string;
}): ISelectListItem[] =>
  sortArrayAlphabetically(
    entities.map(({ id, name }) =>
      toTranslatedSelectListItem({
        id,
        name,
        label: translate(getEntitySingleLabelKey(name), name) || name,
      }),
    ),
    "label",
  );
